import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { Loader2, Library, User, Calendar, Eye, ArrowLeft, BookOpen } from 'lucide-react';
import { Helmet } from 'react-helmet-async';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const LibraryPage = () => {
    const [topics, setTopics] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    useEffect(() => {
        loadLibrary();
    }, []);

    const loadLibrary = async () => {
        try {
            const res = await axios.get(`${API}/library`);
            setTopics(res.data.topics || []);
        } catch (err) {
            setError('Failed to load the published narratives. Please try again later.');
            console.error('Library fetch error:', err);
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="min-h-screen bg-[#02040a]">
            <Helmet>
                <title>Published Narratives | Ai Quran Narratives</title>
                <meta name="description" content="Explore a collection of Quranic Narratives created and published by the Ai Quran community." />
                <link rel="canonical" href="https://aiquran.org/library" />
            </Helmet>

            {/* Header */}
            <header className="border-b border-[#1e293b] bg-[#09090b]/80 backdrop-blur-sm sticky top-0 z-10">
                <div className="max-w-3xl mx-auto px-4 py-4 flex items-center gap-4">
                    <Link to="/" className="flex-shrink-0 text-[#94a3b8] hover:text-white transition-colors">
                        <ArrowLeft className="w-5 h-5" />
                    </Link>
                    <div className="flex items-center gap-3">
                        <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-[#8b5cf6] to-[#6d28d9] flex items-center justify-center">
                            <Library className="w-5 h-5 text-white" />
                        </div>
                        <h1 className="text-xl md:text-2xl font-bold text-[#f8fafc]">
                            Published Narratives
                        </h1>
                    </div>
                </div>
            </header>

            <main className="max-w-3xl mx-auto px-4 py-8">
                <div className="mb-8">
                    <h2 className="text-2xl font-semibold text-[#f8fafc] mb-2">Community Narratives</h2>
                    <p className="text-[#94a3b8]">
                        Browse and explore collections of verses and reflections published by the community. Create your own narratives by tagging verses to different topics.
                    </p>
                </div>

                {loading ? (
                    <div className="flex justify-center p-12">
                        <Loader2 className="w-8 h-8 text-[#8b5cf6] animate-spin" />
                    </div>
                ) : error ? (
                    <div className="text-center p-8 bg-[#ef4444]/10 rounded-xl border border-[#ef4444]/20">
                        <p className="text-[#ef4444]">{error}</p>
                    </div>
                ) : topics.length === 0 ? (
                    <div className="text-center p-12 bg-[#09090b]/50 border border-[#1e293b] rounded-xl">
                        <Library className="w-12 h-12 text-[#1e293b] mx-auto mb-4" />
                        <p className="text-[#94a3b8]">No published topics found.</p>
                    </div>
                ) : (
                    <div className="grid gap-4">
                        {topics.map(topic => (
                            <Link 
                                key={topic.id}
                                to={`/narrative/${topic.id}`}
                                className="group block p-5 rounded-xl border border-[#1e293b] bg-[#09090b]/50 hover:bg-[#1e293b]/50 hover:border-[#8b5cf6]/50 transition-all"
                            >
                                <div className="flex justify-between items-start gap-4">
                                    <div className="flex-1">
                                        <h3 className="text-lg font-medium text-[#f8fafc] group-hover:text-[#a78bfa] transition-colors mb-2">
                                            {topic.name}
                                        </h3>
                                        <div className="flex flex-wrap items-center gap-4 text-xs text-[#64748b]">
                                            <span className="flex items-center gap-1.5 pt-0.5">
                                                <User className="w-3.5 h-3.5" /> 
                                                {topic.author_name || 'Anonymous'}
                                            </span>
                                            {topic.published_at && (
                                                <span className="flex items-center gap-1.5 pt-0.5">
                                                    <Calendar className="w-3.5 h-3.5" /> 
                                                    {new Date(topic.published_at).toLocaleDateString()}
                                                </span>
                                            )}
                                            <span className="flex items-center gap-1.5 pt-0.5">
                                                <Eye className="w-3.5 h-3.5" /> 
                                                {topic.views?.toLocaleString() || 0} views
                                            </span>
                                            <span className="px-2 py-0.5 rounded-full bg-[#1e293b] text-[#94a3b8]">
                                                {topic.verse_count || 0} verses
                                            </span>
                                        </div>
                                    </div>
                                    <div className="hidden sm:flex items-center justify-center w-10 h-10 rounded-full bg-[#1e293b] group-hover:bg-[#8b5cf6]/20 group-hover:text-[#a78bfa] transition-colors">
                                        <BookOpen className="w-4 h-4" />
                                    </div>
                                </div>
                            </Link>
                        ))}
                    </div>
                )}
            </main>
        </div>
    );
};

export default LibraryPage;
